import { Check, ChevronsUpDown } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { getDivisionsForCountry } from "@/lib/division-data"
import { cn } from "@/lib/utils"

interface DivisionComboboxProps {
  country: string
  value: string
  onChange: (value: string) => void
  error?: string
}

export default function DivisionCombobox({
  country,
  value,
  onChange,
  error,
}: DivisionComboboxProps) {
  const [open, setOpen] = useState(false)

  const divisions = getDivisionsForCountry(country)

  return (
    <div className="space-y-1.5">
      <Label>División</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
          >
            {value && value.length > 0 ? (
              value
            ) : (
              <span className="text-muted-foreground">
                Seleccionar división...
              </span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
        >
          <Command>
            <CommandInput
              placeholder="Buscar o escribir división..."
              onValueChange={(search) => {
                if (
                  search &&
                  !divisions.some(
                    (d) => d.toLowerCase() === search.toLowerCase(),
                  )
                ) {
                  onChange(search)
                }
              }}
            />
            <CommandList>
              <CommandEmpty>
                <span className="text-muted-foreground text-sm">
                  Escribí el nombre de tu división
                </span>
              </CommandEmpty>
              <CommandGroup>
                {divisions.map((d) => (
                  <CommandItem
                    key={d}
                    value={d}
                    onSelect={() => {
                      onChange(d)
                      setOpen(false)
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === d ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {d}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  )
}
